/**
 * Centralized redirect utilities for authentication flow
 */

// Maximum allowed path length to prevent URL length issues
const MAX_PATH_LENGTH = 2000;

/**
 * Validate redirectTo path to prevent open redirect and unauthorized access
 * 
 * @param redirectTo - The redirect path to validate
 * @param isAdminUser - Whether the user has admin privileges
 * @returns true if the path is safe to redirect to
 */
export const isValidRedirectPath = (
  redirectTo: string,
  isAdminUser: boolean = false
): boolean => {
  // Decode the path first
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(redirectTo);
  } catch {
    // Invalid encoding
    console.warn('Invalid redirectTo encoding:', redirectTo);
    return false;
  }

  // 1. Must start with / (relative path only, no external URLs)
  if (!decodedPath.startsWith('/')) {
    console.warn('RedirectTo must start with /:', decodedPath);
    return false;
  }

  // 2. No protocol schemes (http://, https://, javascript:, data:, etc.)
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(decodedPath)) {
    console.warn('RedirectTo contains protocol scheme:', decodedPath);
    return false;
  }

  // 3. No path traversal sequences
  if (decodedPath.includes('../') || decodedPath.includes('..\\')) {
    console.warn('RedirectTo contains path traversal:', decodedPath);
    return false;
  }

  // 4. No double slashes (except after protocol, but we already checked for protocols)
  if (decodedPath.includes('//')) {
    console.warn('RedirectTo contains double slashes:', decodedPath);
    return false;
  }

  // 5. Check path length
  if (decodedPath.length > MAX_PATH_LENGTH) {
    console.warn('RedirectTo path too long:', decodedPath.length);
    return false;
  }

  // 6. Check if user is trying to access admin routes without permission
  if (!isAdminUser && decodedPath.startsWith('/admin')) {
    console.warn('Non-admin user attempting to access admin route:', decodedPath);
    return false;
  }

  // 7. Remove dangerous characters (null bytes, control characters)
  if (/[\x00-\x1F\x7F]/.test(decodedPath)) {
    console.warn('RedirectTo contains dangerous characters:', decodedPath);
    return false;
  }

  return true;
};

/**
 * Extract redirectTo path from URL search parameters with validation
 * 
 * @param searchParams - URLSearchParams object or search string
 * @param isAdminUser - Whether the user has admin privileges
 * @returns The validated redirect path or null if invalid
 */
export const getRedirectToFromQuery = (
  searchParams: URLSearchParams | string,
  isAdminUser: boolean = false
): string | null => {
  const params = typeof searchParams === 'string' 
    ? new URLSearchParams(searchParams) 
    : searchParams;
  
  const redirectTo = params.get('redirectTo');
  if (!redirectTo) {
    return null;
  }

  // Validate the redirect path
  if (!isValidRedirectPath(redirectTo, isAdminUser)) {
    // Log invalid attempts for security monitoring (don't expose to user)
    console.warn('Invalid redirectTo path rejected:', redirectTo);
    return null;
  }

  // Decode and return the validated path
  try {
    return decodeURIComponent(redirectTo);
  } catch {
    return null;
  }
};

/**
 * Build login URL with redirectTo query parameter
 * 
 * @param currentPath - The current path (including query params and hash)
 * @returns Login URL with redirectTo query parameter
 */
export const buildLoginUrlWithRedirect = (currentPath: string): string => {
  // Encode the path to handle special characters
  const encodedPath = encodeURIComponent(currentPath);
  return `/login?redirectTo=${encodedPath}`;
};

/**
 * Get the default redirect path based on user role
 * 
 * @param activeRole - The user's active role ('user' or 'admin')
 * @param isAdminUser - Whether the user has admin privileges
 * @returns The default redirect path
 */
export const getDefaultRedirectPath = (
  activeRole: 'user' | 'admin', 
  isAdminUser: boolean
): string => {
  if (activeRole === 'admin' && isAdminUser) {
    return '/admin';
  }
  return '/dashboard';
};

